"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { useFeatureFlagVariantKey } from "@posthog/react";
import { DEFAULT_FLAG_VALUES } from "@/lib/ab-test";
import type { FlagValue } from "@/lib/ab-bootstrap";

// ============================================================================
// Hook A/B — anti-flash
//   `ready` passe à true UNIQUEMENT quand la variante est connue (ou au timeout).
//   Tant que `ready` est false, le composant masque l'UI variant-dépendante pour
//   éviter le "flash" de la variante control avant la bonne variante (FOUC A/B).
// ============================================================================

// Délai max avant de révéler l'UI même si PostHog n'a pas répondu
// (AdBlock / offline) → on affiche alors la variante par défaut.
const REVEAL_TIMEOUT_MS = 1200;

// Flags tranchés par le serveur (voir lib/ab-bootstrap). Présents dès le SSR :
// le HTML sort avec la bonne variante, visible, sans attendre posthog-js.
export const ABBootstrapContext = createContext<Record<string, FlagValue> | null>(null);

export const useABTest = (flagKey: string) => {
  const posthogVariant = useFeatureFlagVariantKey(flagKey);
  const serverFlag = useContext(ABBootstrapContext)?.[flagKey];
  const [override, setOverride] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    // Preview/QA : ?ab=<variant> force la variante affichée (ex. ?ab=obsolescence).
    const forced = new URLSearchParams(window.location.search).get("ab");
    if (forced) setOverride(forced);
    const t = setTimeout(() => setTimedOut(true), REVEAL_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, []);

  // `false`/`true` = flag tranché sans variante → on garde la valeur par défaut.
  const known = posthogVariant !== undefined ? posthogVariant : serverFlag;
  const resolved = override !== null || known !== undefined;
  const variant =
    override ??
    (typeof known === "string" ? known : undefined) ??
    DEFAULT_FLAG_VALUES[flagKey] ??
    "control";

  return {
    variant,
    isControl: variant === "control",
    // prêt à afficher quand PostHog a répondu OU au timeout de secours
    ready: resolved || timedOut,
  };
};
